import React, { useState } from 'react';
import { useInView } from '../hooks/useInView';

const phases = [
  {
    label: 'NOW',
    sub: 'Available today',
    color: '#00C9B1',
    items: [
      'Configurable workflow & engine',
      'MBR authoring + AI vision intake',
      'Veeva connector + outbound ERP queue',
      'Review-by-exception, audit & e-sign',
      'Self-serve BI & dashboards',
    ],
  },
  {
    label: 'NEXT',
    sub: 'On the roadmap',
    color: '#7B3FE4',
    items: [
      'Live MES / LIMS connectors',
      'Inbound ERP connectors (incl. Dynamics AX)',
      'Mobile-native + offline capture',
      'Inspection-readiness command center',
      'Predictive release scoring',
    ],
  },
  {
    label: 'VISION',
    sub: 'The horizon',
    color: '#F5A623',
    items: [
      'Multi-site quality control tower',
      'Advanced multivariate AI',
      'Regulatory submission automation',
      'Module marketplace',
    ],
  },
];

function DemoModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', role: '', sites: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    if (!form.company.trim()) e.company = 'Required';
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSubmitted(true);
  };

  const field = (key, label, placeholder, type = 'text', required = false) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.04em' }}>
        {label}{required && <span style={{ color: '#00C9B1', marginLeft: 3 }}>*</span>}
      </label>
      <input
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={e => { setForm(f => ({ ...f, [key]: e.target.value })); setErrors(er => ({ ...er, [key]: undefined })); }}
        style={{
          background: 'rgba(255,255,255,0.05)',
          border: `1px solid ${errors[key] ? '#E74C3C' : 'rgba(255,255,255,0.1)'}`,
          borderRadius: 9, padding: '11px 14px',
          fontSize: 14, color: '#fff',
          outline: 'none', fontFamily: 'Inter, sans-serif',
          transition: 'border-color 0.2s',
        }}
        onFocus={e => { if (!errors[key]) e.target.style.borderColor = 'rgba(0,201,177,0.5)'; }}
        onBlur={e => { if (!errors[key]) e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
      />
      {errors[key] && <span style={{ fontSize: 11, color: '#E74C3C' }}>{errors[key]}</span>}
    </div>
  );

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        animation: 'fadeIn 0.2s ease',
      }}
    >
      <div style={{
        background: '#161B24',
        border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: 20,
        padding: '36px 36px 32px',
        width: '100%', maxWidth: 520,
        maxHeight: '90vh', overflowY: 'auto',
        position: 'relative',
        animation: 'slideUp 0.25s ease',
        boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,201,177,0.06)',
      }}>
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16,
            width: 32, height: 32, borderRadius: 8,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.5)', fontSize: 16, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s, color 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
        >✕</button>

        {submitted ? (
          /* Success state */
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'linear-gradient(135deg, #00C9B1, #7B3FE4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, margin: '0 auto 24px',
              boxShadow: '0 0 40px rgba(0,201,177,0.3)',
            }}>✓</div>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: '#fff', marginBottom: 10, letterSpacing: '-0.02em' }}>
              We'll be in touch!
            </h3>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, marginBottom: 28 }}>
              Thanks, <strong style={{ color: '#fff' }}>{form.name}</strong>. Someone from the Qualicore team will reach out to <strong style={{ color: '#00C9B1' }}>{form.email}</strong> within one business day to schedule your pilot.
            </p>
            <button onClick={onClose} style={{
              padding: '12px 28px', fontSize: 14, fontWeight: 700,
              background: 'linear-gradient(135deg, #00C9B1, #7B3FE4)',
              color: '#fff', borderRadius: 10, cursor: 'pointer',
            }}>Close</button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'linear-gradient(135deg, #00C9B1, #7B3FE4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 800, color: '#fff',
                }}>Q</div>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>Book a Demo</span>
              </div>
              <h2 style={{ fontSize: 26, fontWeight: 900, color: '#fff', letterSpacing: '-0.02em', marginBottom: 6 }}>
                Shape Qualicore to your process.
              </h2>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6 }}>
                Pilot in weeks, not quarters. Fill in a few details and we'll set up a tailored session.
              </p>
            </div>

            {/* Form fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {field('name', 'Full Name', 'Jane Smith', 'text', true)}
                {field('email', 'Work Email', 'jane@pharma.com', 'email', true)}
              </div>
              {field('company', 'Company', 'Acme Pharmaceuticals', 'text', true)}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.04em' }}>Your Role</label>
                  <select
                    value={form.role}
                    onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 9, padding: '11px 14px',
                      fontSize: 14, color: form.role ? '#fff' : 'rgba(255,255,255,0.3)',
                      outline: 'none', fontFamily: 'Inter, sans-serif', cursor: 'pointer',
                    }}
                  >
                    <option value="" style={{ background: '#161B24' }}>Select role…</option>
                    {['QA Director', 'QP / Qualified Person', 'VP Quality', 'IT / Digital', 'Operations', 'Other'].map(r => (
                      <option key={r} value={r} style={{ background: '#161B24', color: '#fff' }}>{r}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.04em' }}>No. of Sites</label>
                  <select
                    value={form.sites}
                    onChange={e => setForm(f => ({ ...f, sites: e.target.value }))}
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 9, padding: '11px 14px',
                      fontSize: 14, color: form.sites ? '#fff' : 'rgba(255,255,255,0.3)',
                      outline: 'none', fontFamily: 'Inter, sans-serif', cursor: 'pointer',
                    }}
                  >
                    <option value="" style={{ background: '#161B24' }}>Select…</option>
                    {['1', '2–5', '6–15', '15+'].map(s => (
                      <option key={s} value={s} style={{ background: '#161B24', color: '#fff' }}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.04em' }}>Anything specific you'd like to cover? <span style={{ color: 'rgba(255,255,255,0.25)' }}>(optional)</span></label>
                <textarea
                  value={form.message}
                  placeholder="e.g. EU Annex 16 QP certification, SAP integration, legacy BPR migration…"
                  rows={3}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 9, padding: '11px 14px',
                    fontSize: 14, color: '#fff', resize: 'vertical',
                    outline: 'none', fontFamily: 'Inter, sans-serif',
                    lineHeight: 1.6,
                  }}
                  onFocus={e => { e.target.style.borderColor = 'rgba(0,201,177,0.5)'; }}
                  onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                style={{
                  marginTop: 4,
                  padding: '14px', fontSize: 15, fontWeight: 700,
                  background: 'linear-gradient(135deg, #00C9B1, #7B3FE4)',
                  color: '#fff', borderRadius: 11, cursor: 'pointer',
                  boxShadow: '0 4px 24px rgba(0,201,177,0.25)',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  border: 'none', width: '100%',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,201,177,0.35)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,201,177,0.25)'; }}
              >
                Book My Demo →
              </button>

              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', textAlign: 'center', lineHeight: 1.5 }}>
                No spam. A real human will reach out within 1 business day.
              </p>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        input::placeholder, textarea::placeholder { color: rgba(255,255,255,0.2); }
      `}</style>
    </div>
  );
}

export default function Roadmap({ showModal, setShowModal }) {
  const [ref, inView] = useInView();
  

  return (
    <section id="roadmap" style={{
      padding: '120px 24px 160px',
      background: 'linear-gradient(180deg, transparent, rgba(22,27,36,0.6))',
    }}>
      {showModal && <DemoModal onClose={() => setShowModal(false)} />}

      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div ref={ref} style={{
          textAlign: 'center', marginBottom: 72,
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}>
          <p style={{
            fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
            color: '#00C9B1', marginBottom: 14, fontFamily: "'JetBrains Mono', monospace",
          }}>Roadmap</p>
          <h2 style={{
            fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 900, lineHeight: 1.08,
            letterSpacing: '-0.03em', color: '#fff', marginBottom: 16,
          }}>Extends to any extreme.</h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.5)', maxWidth: 500, margin: '0 auto' }}>
            A configurable core, open data model, and connector SDK grow Qualicore — never a re-platform.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 20, marginBottom: 72,
        }}>
          {phases.map((phase, i) => {
            const [pRef, pInView] = useInView();
            return (
              <div key={phase.label} ref={pRef} style={{
                background: `linear-gradient(180deg, ${phase.color}10, ${phase.color}05)`,
                border: `1px solid ${phase.color}25`,
                borderRadius: 20, padding: '32px 28px', overflow: 'hidden',
                position: 'relative',
                opacity: pInView ? 1 : 0,
                transform: pInView ? 'translateY(0)' : 'translateY(28px)',
                transition: `opacity 0.6s ease ${i * 120}ms, transform 0.6s ease ${i * 120}ms`,
              }}>
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                  background: phase.color,
                }} />
                <div style={{
                  display: 'inline-block',
                  background: phase.color,
                  color: phase.label === 'VISION' ? '#000' : '#fff',
                  fontSize: 12, fontWeight: 800, letterSpacing: '0.1em',
                  padding: '4px 14px', borderRadius: 100, marginBottom: 8,
                }}>{phase.label}</div>
                <p style={{
                  fontSize: 13, color: phase.color, fontWeight: 600, marginBottom: 24,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>{phase.sub}</p>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {phase.items.map(item => (
                    <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ color: phase.color, fontSize: 14, marginTop: 1, flexShrink: 0 }}>→</span>
                      <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <p style={{
          fontSize: 12, color: 'rgba(255,255,255,0.3)',
          fontFamily: "'JetBrains Mono', monospace",
          textAlign: 'center', marginBottom: 80,
        }}>
          NEXT and HORIZON items are roadmap, not available today; sequencing may change.
        </p>

        {/* CTA Banner */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(0,201,177,0.12), rgba(123,63,228,0.12))',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 24, padding: '48px 40px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 24,
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: -60, right: -60,
            width: 200, height: 200, borderRadius: '50%',
            background: 'rgba(0,201,177,0.08)', filter: 'blur(40px)',
          }} />
          <div>
            <h3 style={{
              fontSize: 'clamp(22px, 3.5vw, 36px)', fontWeight: 900,
              letterSpacing: '-0.02em', color: '#fff', marginBottom: 10,
            }}>Let's shape Qualicore to your process.</h3>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }}>
              Sites & jurisdictions — pilot in weeks, not quarters.
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0,
              padding: '16px 36px', fontSize: 16, fontWeight: 700,
              background: 'linear-gradient(135deg, #00C9B1, #7B3FE4)',
              color: '#fff', borderRadius: 12, cursor: 'pointer', border: 'none',
              boxShadow: '0 4px 32px rgba(0,201,177,0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              zIndex: 1, fontFamily: 'Inter, sans-serif',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 40px rgba(0,201,177,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 32px rgba(0,201,177,0.3)'; }}
          >
            Book a Demo →
          </button>
        </div>
      </div>
    </section>
  );
}
